import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, addDoc, deleteDoc, doc, setDoc, updateDoc } from 'firebase/firestore';
import QRCode from 'qrcode';
import { db, auth } from './firebase';
import TimeSelectModal from './TimeSelectModal';
import LimitCheckModal from './LimitCheckModal';
import { NORMAL_APPOINTMENT_TYPE, getAppointmentTypeLabel } from './appointmentTypes';
import { generateBookingDates, isSundayDateString, isWithinOpenHours } from './bookingSchedule';
import { getWashPriceAmount } from './washPackages';

function Appointment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceData } = location.state || {};
  const isExpired = location.state?.expired || false;
  const shouldResetBooking = location.state?.resetBooking || false;

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [occupiedTimeSlots, setOccupiedTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [appointmentType, setAppointmentType] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [showDateSelection, setShowDateSelection] = useState(false);
  const [canBookNormalAppointment, setCanBookNormalAppointment] = useState(true);
  const [loadingNormalCheck, setLoadingNormalCheck] = useState(false);
  const [normalBlockDetails, setNormalBlockDetails] = useState(null);
  const [canBookSpecialAppointment, setCanBookSpecialAppointment] = useState(true);
  const [loadingSpecialCheck, setLoadingSpecialCheck] = useState(false);
  const [specialBlockDetails, setSpecialBlockDetails] = useState(null);
  const [orderId, setOrderId] = useState('');
  const [description, setDescription] = useState('');
  const [numberPlate, setNumberPlate] = useState('');
  const [hasSpecialRebookState, setHasSpecialRebookState] = useState(false);
  
  // Generate random 4-digit order code
  const generateOrderCode = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  // Check if user has contact number and return contact data
  const checkUserContact = async () => {
    if (!auth.currentUser) {
      return { hasContact: false, message: 'User not authenticated', contact: null };
    }

    try {
      const userDocRef = doc(db, "users", auth.currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const hasContact = userData.contact && userData.contact.trim() !== '';
        
        return { 
          hasContact, 
          contact: userData.contact || null,
          message: hasContact ? 'Contact found' : 'No contact number found. Please add your contact number in your account settings before booking an appointment.'
        };
      } else {
        return { hasContact: false, contact: null, message: 'User profile not found. Please complete your profile setup.' };
      }
    } catch (error) {
      console.error('Error checking user contact:', error);
      return { hasContact: false, contact: null, message: 'Error checking contact information. Please try again.' };
    }
  };

  // Check if number plate is already booked for the appointment date
  const checkNumberPlateAvailability = async (numberPlate, appointmentDate) => {
    if (!numberPlate || numberPlate.trim() === '') {
      return { isAvailable: true, message: 'No number plate provided' };
    }

    try {
      const normalizedNumberPlate = numberPlate.trim().toUpperCase();
      const ordersQuery = query(
        collection(db, 'orders'),
        where('appointmentDate', '==', appointmentDate),
        where('numberPlate', '==', normalizedNumberPlate),
        where('status', 'in', ['confirmed', 'completed'])
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      
      if (ordersSnapshot.empty) {
        return { isAvailable: true, message: 'Number plate is available' };
      } else {
        const conflictingOrder = ordersSnapshot.docs[0].data();
        return { 
          isAvailable: false, 
          message: `Number plate ${normalizedNumberPlate} is already booked for ${appointmentDate}. Please choose a different number plate or date.`
        };
      }
    } catch (error) {
      console.error('Error checking number plate availability:', error);
      return { isAvailable: false, message: 'Error checking number plate availability. Please try again.' };
    }
  };
  const [hasUsedRebookState, setHasUsedRebookState] = useState(() => {
    // Check if we've already used the rebook state in this session
    const userId = auth.currentUser?.uid;
    const storageKey = `rebook_state_used_${userId}`;
    const isUsed = sessionStorage.getItem(storageKey) === 'true';
    console.log('🔍 Initializing hasUsedRebookState:', {
      userId,
      storageKey,
      isUsed,
      allSessionStorage: Object.keys(sessionStorage).filter(key => key.includes('rebook')),
      locationState: location.state
    });
    
    // If we're coming from a rebook, don't use the sessionStorage value
    if (location.state?.isRebook) {
      console.log('🔄 Coming from rebook - ignoring sessionStorage value');
      return false;
    }
    
    // Also ignore if we have a special rebook state (meaning we can still use it)
    if (location.state?.specialIsFree) {
      console.log('🔄 Special rebook state detected - ignoring sessionStorage value');
      return false;
    }
    
    return isUsed;
  });
  
  // Special appointment confirmation dialog state
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [pendingTimeSelection, setPendingTimeSelection] = useState('');
  const [userSpecialBookings, setUserSpecialBookings] = useState({});
  
  // Track where user came from for better redirects
  const [cameFromServices, setCameFromServices] = useState(false);
  
  // Session tracking refs
  const sessionId = useRef(null);
  const isSessionActive = useRef(false);
  const sessionCheckInterval = useRef(null);
  const storageEventListener = useRef(null);
  
  // Generate unique page load ID to detect refreshes
  const pageLoadId = useRef(`page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Flag to track if page is being unloaded properly
  const isUnloading = useRef(false);
  // Flag to indicate we're navigating to payment (still in booking process)
  const continuingBookingProcess = useRef(false);

  // Listen for sessionStorage changes to update hasUsedRebookState
  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    const storageKey = `rebook_state_used_${userId}`;
    const checkRebookState = () => {
      const isUsed = sessionStorage.getItem(storageKey) === 'true';
      
      // If we're coming from a rebook or have special rebook state, ignore sessionStorage
      if (location.state?.isRebook || location.state?.specialIsFree) {
        console.log('🔄 Ignoring sessionStorage change - coming from rebook or special rebook state');
        setHasUsedRebookState(false);
        return;
      }
      
      // Only log and update if the state actually changed
      if (isUsed !== hasUsedRebookState) {
        console.log('🔄 SessionStorage changed - updating hasUsedRebookState:', {
          storageKey,
          isUsed,
          currentState: hasUsedRebookState
        });
        setHasUsedRebookState(isUsed);
      }
    };

    // Check initial state
    checkRebookState();

    // Listen for storage events (when sessionStorage changes in other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === storageKey) {
        console.log('🔄 Storage event detected for rebook state:', e);
        checkRebookState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [location.state]);

  // Generate a unique order ID
  const generateOrderId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `ORD${timestamp}${random}`.toUpperCase();
  };
  
  // Flag to prevent multiple redirects
  const hasRedirected = useRef(false);
  // Modal state
  const [isTimeSelectModalOpen, setIsTimeSelectModalOpen] = useState(false);
  const [isLimitCheckModalOpen, setIsLimitCheckModalOpen] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState(null);
  
  // Helper function to handle redirect cleanup
  const handleRedirectCleanup = () => {
    if (hasRedirected.current) {
      console.log('Already redirected, skipping additional redirect');
      return true; // Already redirected
    }
    
    hasRedirected.current = true;
    
    // Clean up sessions immediately before redirect
    if (auth.currentUser) {
      const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      // Clean up browser session
      sessionStorage.removeItem(currentSessionKey);
      console.log('Browser session cleaned up before redirect');
      
      // Clean up global session if this tab owns it
      const globalSession = localStorage.getItem(globalSessionKey);
      if (globalSession) {
        try {
          const globalSessionData = JSON.parse(globalSession);
          if (globalSessionData.ownerTabId === pageLoadId.current && globalSessionData.page === 'appointment') {
            localStorage.removeItem(globalSessionKey);
            console.log('Global appointment session cleaned up by owner tab before redirect');
          }
        } catch (error) {
          console.warn('Error parsing global session before redirect:', error);
          localStorage.removeItem(globalSessionKey);
        }
      }
    }
    
    // REMOVED: Active booking session cleanup - no longer needed
    
    // Stop all intervals and cleanup to prevent further conflicts
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
    
    // Reset session tracking
    isSessionActive.current = false;
    sessionId.current = null;
    
    return false; // Not redirected yet
  };



  const checkSpecialAppointmentBlock = async () => {
    // This function is no longer needed since we use Cloud Function for all checks
    return { blocked: false };
  };

  // Check if user can book a normal appointment
  const checkCanBookNormalAppointment = async () => {
    if (!auth.currentUser) {
      return true;
    }

    try {
      setLoadingNormalCheck(true);
      console.log('🔍 Checking if user can book free appointment...');
      
      // Use Cloud Function to check eligibility
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('./firebase');
      const checkFreeEligibility = httpsCallable(functions, 'checkFreeAppointmentEligibility');
      
      console.log('🔍 Calling Cloud Function with userId:', auth.currentUser.uid);
      console.log('🔍 Cloud Function call starting...');
      const result = await checkFreeEligibility({ userId: auth.currentUser.uid });
      console.log('🔍 Cloud Function call completed successfully');
      const data = result.data;
      
      console.log('🔍 Cloud Function result:', data);
      console.log('🔍 Cloud Function result type:', typeof data);
      console.log('🔍 Cloud Function result.canBook:', data.canBook);
      
      const canBook = data.canBook;
      console.log('✅ Can book free appointment:', canBook);
      setCanBookNormalAppointment(canBook);
      setNormalBlockDetails(data.details || null);
      return canBook;
    } catch (error) {
      console.error('❌ Error checking free appointment eligibility:', error);
      
      // Only allow booking for specific permission errors, not all errors
      if (error.code === 'permission-denied' || error.code === 'unavailable') {
        console.log('⚠️ Due to permission issues, allowing free appointment booking');
        setCanBookNormalAppointment(true);
        return true;
        } else {
        console.log('❌ Blocking free appointment booking due to error');
        setCanBookNormalAppointment(false);
        setNormalBlockDetails('Unable to verify eligibility. Please try again later.');
        return false;
      }
    } finally {
      setLoadingNormalCheck(false);
    }
  };

  // Check if user can book a special appointment
  const checkCanBookSpecialAppointment = async () => {
    if (!auth.currentUser) {
      return true;
    }

    try {
      setLoadingSpecialCheck(true);

      console.log('🔍 Checking if user can book special appointment...');
      console.log('🔍 User ID:', auth.currentUser.uid);
      
      // Use Cloud Function to check eligibility
      console.log('🔍 About to import firebase/functions');
      const { httpsCallable } = await import('firebase/functions');
      console.log('🔍 About to import firebase functions');
      const { functions } = await import('./firebase');
      console.log('🔍 Creating httpsCallable');
      const checkSpecialEligibility = httpsCallable(functions, 'checkSpecialAppointmentEligibility');
      
              console.log('🔍 Calling Cloud Function with userId:', auth.currentUser.uid);
        console.log('🔍 Cloud Function call starting...');
        try {
          const result = await checkSpecialEligibility({ userId: auth.currentUser.uid });
          console.log('🔍 Cloud Function call completed successfully');
          console.log('🔍 Cloud Function call successful, result:', result);
          const data = result.data;
        
          console.log('🔍 Cloud Function result:', data);
          console.log('🔍 Cloud Function result type:', typeof data);
          console.log('🔍 Cloud Function result.canBook:', data.canBook);
        
        const canBook = data.canBook;
      
        console.log('✅ Can book special appointment:', canBook);
        setCanBookSpecialAppointment(canBook);
        setSpecialBlockDetails(data.details || null);
        return canBook;
      } catch (functionError) {
        console.error('❌ Cloud Function call failed:', functionError);
        throw functionError;
      }
    } catch (error) {
      console.error('❌ Error checking special appointment eligibility:', error);
      
      // Only allow booking for specific permission errors, not all errors
      if (error.code === 'permission-denied' || error.code === 'unavailable') {
        console.log('⚠️ Due to permission issues, allowing special appointment booking');
        setCanBookSpecialAppointment(true);
      return true;
      } else {
        console.log('❌ Blocking special appointment booking due to error');
        setCanBookSpecialAppointment(false);
        setSpecialBlockDetails('Unable to verify eligibility. Please try again later.');
        return false;
      }
    } finally {
      setLoadingSpecialCheck(false);
    }
  };



  // Generate available dates (filtered version)
  const generateAvailableDates = () => generateBookingDates(formatDate);

  // Load available dates when appointment type is selected
  const handleAppointmentTypeChange = async (newType) => {
    console.log('🔄 handleAppointmentTypeChange called with:', newType);
    
    setAppointmentType(newType);
    setSelectedDate('');
    setSelectedTime('');
    setShowDateSelection(false);
    
    // Auto-populate order ID and description for normal appointments
    if (newType === NORMAL_APPOINTMENT_TYPE) {
      setOrderId(generateOrderId());
      const washName = serviceData?.name || 'Car Wash Service';
      setDescription(`${washName} - ${getAppointmentTypeLabel(NORMAL_APPOINTMENT_TYPE)}`);
    } else {
      // Clear fields for other appointment types
      setOrderId('');
      setDescription('');
    }
    
    // Check eligibility when appointment type changes and generate dates
    if (newType) {
      setLoadingDates(true);
      try {
        // Check eligibility first
        if (newType === NORMAL_APPOINTMENT_TYPE && auth.currentUser) {
          console.log('🔄 Checking normal appointment eligibility...');
          await checkCanBookNormalAppointment();
        } else if (newType === 'special' && auth.currentUser) {
          console.log('🔄 Checking special appointment eligibility...');
          await checkCanBookSpecialAppointment();
        }
        
        // Generate dates
        console.log('🔄 Loading dates for appointment type:', newType);
        const dates = generateAvailableDates();
        setAvailableDates(dates);
        setShowDateSelection(true);
      } catch (error) {
        console.error('❌ Error loading dates:', error);
        setAvailableDates([]);
      } finally {
        setLoadingDates(false);
      }
    }
  };

  // Check appointment eligibility on mount and when user changes
  useEffect(() => {
    if (auth.currentUser) {
      checkCanBookNormalAppointment();
      checkCanBookSpecialAppointment();
      checkSpecialRebookState();
    }
  }, [auth.currentUser]);

  // Check for special rebook state in database
  const checkSpecialRebookState = async () => {
    if (!auth.currentUser) {
      console.log('❌ No auth.currentUser, skipping rebook state check');
      return;
    }
    
    try {
      console.log('🔍 Checking special rebook state for user:', auth.currentUser.uid);
      const rebookStatesQuery = query(
        collection(db, 'specialRebookStates'),
        where('userId', '==', auth.currentUser.uid),
        where('rebookType', '==', 'special')
      );
      
      console.log('🔍 Executing query...');
      const querySnapshot = await getDocs(rebookStatesQuery);
      console.log('📊 Found rebook states:', querySnapshot.size);
      
      if (!querySnapshot.empty) {
        // User has an active rebook state, set the flag
        const rebookState = querySnapshot.docs[0].data();
        console.log('📋 Rebook state data:', rebookState);
        
        const expiresAt = rebookState.expiresAt?.toDate?.() || new Date(rebookState.expiresAt);
        const now = new Date();
        
        console.log('⏰ Rebook state expires at:', expiresAt);
        console.log('⏰ Current time:', now);
        console.log('⏰ Is expired?', expiresAt <= now);
        
        if (expiresAt > now) {
          // Rebook state is still valid
          console.log('✅ Active special rebook state found - setting to true');
          setHasSpecialRebookState(true);
        } else {
          // Rebook state has expired, clean it up
          console.log('⏰ Rebook state expired - cleaning up');
          await deleteDoc(doc(db, 'specialRebookStates', querySnapshot.docs[0].id));
          console.log('✅ Expired rebook state deleted - setting to false');
          setHasSpecialRebookState(false);
        }
      } else {
        // No rebook state found, ensure it's set to false
        console.log('❌ No active special rebook state found - setting to false');
        setHasSpecialRebookState(false);
      }
    } catch (error) {
      console.error('❌ Error checking special rebook state:', error);
      console.error('❌ Error details:', error.code, error.message);
      // On error, assume no rebook state
      setHasSpecialRebookState(false);
    }
  };



  // Check occupied time slots when appointment type changes
  // Check timeslots when date is selected
  useEffect(() => {
    if (selectedDate && selectedLocation === 'Chaguanas Branch' && appointmentType) {
      checkOccupiedTimeSlots(selectedDate);
    }
  }, [selectedDate, selectedLocation]); // When date or location changes

  // Check timeslots when appointment type changes (only if date is already selected)
  useEffect(() => {
    if (selectedDate && selectedLocation === 'Chaguanas Branch' && appointmentType) {
      checkOccupiedTimeSlots(selectedDate);
    }
  }, [appointmentType]); // Only when appointment type changes

  // Check special rebook state when appointment type changes to special
  useEffect(() => {
    if (appointmentType === 'special' && auth.currentUser) {
      checkSpecialRebookState();
    } else if (appointmentType !== 'special') {
      // Clear the state when appointment type is not special
      setHasSpecialRebookState(false);
    }
  }, [appointmentType]);

        // Clear rebook state when component mounts (if not coming from rebook)
  useEffect(() => {
    console.log('🔄 Component mount useEffect - location.state:', location.state);
    console.log('🔄 Component mount useEffect - location.state?.isRebook:', location.state?.isRebook);
    console.log('🔄 Component mount useEffect - location.state?.specialIsFree:', location.state?.specialIsFree);
    
    if (auth.currentUser) {
      if (!location.state?.isRebook) {
      console.log('🔄 Component mounted - clearing any existing rebook state');
      setHasSpecialRebookState(false);
      // Also clear the used rebook state if not coming from rebook
      sessionStorage.removeItem(`rebook_state_used_${auth.currentUser.uid}`);
      setHasUsedRebookState(false);
      } else {
        console.log('🔄 Component mounted - coming from rebook, ensuring rebook state is available');
        // If coming from rebook, ensure we don't have a used rebook state
        sessionStorage.removeItem(`rebook_state_used_${auth.currentUser.uid}`);
        setHasUsedRebookState(false);
        // Also check for the actual rebook state in the database
        checkSpecialRebookState();
      }
    }
  }, []);

  // Debug useEffect to track hasUsedRebookState changes
  useEffect(() => {
    console.log('🔄 hasUsedRebookState changed to:', hasUsedRebookState);
    if (auth.currentUser) {
      const storageKey = `rebook_state_used_${auth.currentUser.uid}`;
      console.log('💾 Current sessionStorage value:', storageKey, '=', sessionStorage.getItem(storageKey));
    }
  }, [hasUsedRebookState]);

  // Debug useEffect to track hasSpecialRebookState changes
  useEffect(() => {
    console.log('🔄 hasSpecialRebookState changed to:', hasSpecialRebookState);
  }, [hasSpecialRebookState]);

  // Debug useEffect to track appointmentType changes
  useEffect(() => {
    console.log('🔄 appointmentType changed to:', appointmentType);
  }, [appointmentType]);

  // Available locations
  const locations = [
    { value: 'Chaguanas Branch', label: 'Chaguanas Branch' }
  ];


  // Helper function to format dates in DD/MM/YYYY format (using local timezone)
  const formatDate = (date) => {
    if (!date) return '';
    let d;
    if (date.toDate) {
      // Firestore timestamp
      d = date.toDate();
    } else if (date instanceof Date) {
      // Already a Date object
      d = date;
    } else {
      // String or timestamp
      d = new Date(date);
    }
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  // Available time slots - generated below; normal = 15 min, special = :00/:30
  // Sunday: 11:15 AM – 6:50 PM; other days: after 7:00 AM – 6:50 PM
  const buildAllTimeSlots = (type, dateStr = selectedDate) => {
    const slots = [];
    const interval = type === 'special' ? 10 : 15;
    const sunday = isSundayDateString(dateStr);
    for (let hour = 7; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        if (hour === 7 && minute === 0) continue;
        if (type === 'special' && minute !== 0 && minute !== 30) continue;
        if (!isWithinOpenHours(hour, minute, sunday)) continue;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        const value = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ value, label: value });
      }
    }
    return slots;
  };

  // Get available time slots based on selected date
  const getAvailableTimeSlots = () => {
    if (!selectedDate) return buildAllTimeSlots(appointmentType);
    
    const today = new Date();
    const selectedDateObj = new Date(selectedDate + 'T00:00:00');
    const isToday = selectedDateObj.toDateString() === today.toDateString();
    
    let availableSlots = buildAllTimeSlots(appointmentType);
    
    // For today, filter out past times and times within 30 minutes
    if (isToday) {
      const now = new Date();
      
      // Add 30 minutes to current time
      const cutoffTime = new Date(now.getTime() + 30 * 60 * 1000);
      const cutoffMinutes = cutoffTime.getHours() * 60 + cutoffTime.getMinutes();
      
      console.log('🔍 Time filtering enabled for today:', {
        selectedDate,
        currentTime: now.toLocaleTimeString(),
        cutoffTime: cutoffTime.toLocaleTimeString(),
        cutoffMinutes
      });
      
      availableSlots = availableSlots.filter(slot => {
        const match = slot.value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match) return true;
        let hour = parseInt(match[1], 10);
        const minute = parseInt(match[2], 10);
        const ampm = match[3].toUpperCase();
        if (ampm === 'PM' && hour !== 12) hour += 12;
        if (ampm === 'AM' && hour === 12) hour = 0;
        const slotMinutes = hour * 60 + minute;
        const shouldKeep = slotMinutes > cutoffMinutes;
        if (!shouldKeep) {
          console.log(`⏰ Filtering out time slot ${slot.value}`);
        }
        return shouldKeep;
      });
    }
    
    // Filter out occupied time slots for Chaguanas Branch location
    if (selectedLocation === 'Chaguanas Branch' && occupiedTimeSlots.length > 0 && appointmentType) {
      console.log('🔍 Filtering out occupied time slots:', occupiedTimeSlots);
      availableSlots = availableSlots.filter(slot => {
        const isOccupied = occupiedTimeSlots.includes(slot.value);
        if (isOccupied) {
          console.log('🚫 Filtering out occupied slot:', slot.value);
        }
        return !isOccupied;
      });
    }

    console.log('✅ Final available slots count:', availableSlots.length);
    console.log('✅ Available slots:', availableSlots.slice(0, 5).map(slot => slot.value)); // Show first 5 slots
    return availableSlots;
  };

  // Check for occupied time slots when date is selected
  const checkOccupiedTimeSlots = async (selectedDate) => {
    console.log('🔍 checkOccupiedTimeSlots called with:', { selectedDate, selectedLocation, appointmentType });
    
    if (!selectedDate || selectedLocation !== 'Chaguanas Branch') {
      console.log('❌ Skipping occupied time slots check - no date or wrong location');
      console.log('❌ selectedDate:', selectedDate, 'selectedLocation:', selectedLocation);
      return;
    }
    
    // Only check for occupied time slots if appointment type is selected
    if (!appointmentType) {
      console.log('❌ Skipping occupied time slots check - no appointment type');
      setOccupiedTimeSlots([]);
      setLoadingTimeSlots(false);
      return;
    }
    
    console.log('🔍 Checking occupied time slots for date:', selectedDate, 'appointment type:', appointmentType);
    setLoadingTimeSlots(true);
    
    try {
      // Check if user is authenticated before making Firestore queries
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('❌ User not authenticated, skipping Firestore queries');
        setOccupiedTimeSlots([]);
        setLoadingTimeSlots(false);
        return;
      }
      
      // DATABASE-DRIVEN: Check both orders and active sessions
      console.log('📊 Checking database for existing orders and active sessions on:', selectedDate);
      
      // Get orders for this date, location, and appointment type
      const ordersQuery = query(
        collection(db, 'orders'),
        where('appointmentDate', '==', selectedDate),
        where('location', '==', 'Chaguanas Branch'),
        where('appointmentType', '==', appointmentType)
      );
      
      // Get active sessions for this date and location (not expired) - temporarily without appointment type filter
      const now = new Date();
      console.log('🔍 Current time for active sessions query:', now);
      const activeSessionsQuery = query(
        collection(db, 'activeBookings'),
        where('appointmentDate', '==', selectedDate),
        where('location', '==', 'Chaguanas Branch'),
        where('expiresAt', '>', now)
      );
      
      let ordersSnapshot;
      let activeSessionsSnapshot;
      
      try {
        ordersSnapshot = await getDocs(ordersQuery);
        console.log('✅ Found', ordersSnapshot.size, 'total orders for this date');
      } catch (error) {
        console.error('❌ Error fetching orders:', error);
        console.log('⚠️ Due to permission issues, allowing all time slots');
        setOccupiedTimeSlots([]);
        setLoadingTimeSlots(false);
        return;
      }
      
      try {
        console.log('🔍 Executing active sessions query...');
        activeSessionsSnapshot = await getDocs(activeSessionsQuery);
        console.log('✅ Found', activeSessionsSnapshot.size, 'active sessions for this date');
        
        // Debug: Log all active sessions found
        activeSessionsSnapshot.forEach((doc, index) => {
          const data = doc.data();
          console.log(`🔍 Active session ${index + 1}:`, {
            id: doc.id,
            appointmentDate: data.appointmentDate,
            appointmentTime: data.appointmentTime,
            location: data.location,
            expiresAt: data.expiresAt,
            userId: data.userId
          });
        });
      } catch (error) {
        console.warn('⚠️ Could not fetch active sessions (permission issue):', error);
        activeSessionsSnapshot = { forEach: () => {} }; // Empty snapshot
      }
      
      // Count orders by time slot (excluding completed orders)
      const orderCounts = {};
      ordersSnapshot.forEach((doc) => {
        const orderData = doc.data();
        console.log('📝 Order found:', {
          time: orderData.appointmentTime,
          status: orderData.status,
          type: orderData.appointmentType,
          userId: orderData.userId
        });
        
        // Don't count completed orders for timeslot blocking
        if (orderData.appointmentTime && orderData.status !== 'completed') {
          orderCounts[orderData.appointmentTime] = (orderCounts[orderData.appointmentTime] || 0) + 1;
        }
      });
      
      // Count active sessions by time slot (ALL sessions from all users)
      const sessionCounts = {};
      const currentUserSessions = {}; // Track current user's sessions separately
      const tradingOrderReservations = new Set(); // Track timeslots with trading order reservations
      
      activeSessionsSnapshot.forEach((doc) => {
        const sessionData = doc.data();
        console.log('📝 Active session found:', {
          time: sessionData.appointmentTime,
          userId: sessionData.userId,
          appointmentType: sessionData.appointmentType,
          type: sessionData.type,
          expiresAt: sessionData.expiresAt
        });
        
        // Check for trading order reservations (these should block the timeslot completely)
        if (sessionData.type === 'trading_order_reservation' && sessionData.appointmentTime) {
          tradingOrderReservations.add(sessionData.appointmentTime);
          console.log('🚫 Trading order reservation found for time slot:', sessionData.appointmentTime);
        }
        
        // Only count regular booking sessions that match the current appointment type
        if (sessionData.appointmentTime && sessionData.appointmentType === appointmentType && sessionData.type !== 'trading_order_reservation') {
          sessionCounts[sessionData.appointmentTime] = (sessionCounts[sessionData.appointmentTime] || 0) + 1;
          
          // Track current user's sessions separately for blocking logic
          if (sessionData.userId === currentUser.uid) {
            currentUserSessions[sessionData.appointmentTime] = (currentUserSessions[sessionData.appointmentTime] || 0) + 1;
            console.log('📝 Current user session for time slot:', sessionData.appointmentTime);
          } else {
            console.log('📝 Other user session for time slot:', sessionData.appointmentTime);
          }
        } else if (sessionData.appointmentTime && sessionData.type !== 'trading_order_reservation') {
          console.log('📝 Ignoring session - wrong appointment type:', sessionData.appointmentType, 'vs current:', appointmentType);
        }
      });
      
      console.log('📊 Order counts by time slot:', orderCounts);
      console.log('📊 Active session counts by time slot:', sessionCounts);
      console.log('🚫 Trading order reservations:', Array.from(tradingOrderReservations));
      console.log('🔍 Current user ID:', currentUser.uid);
      
      // Determine which time slots should be blocked
      const blockedTimeSlots = [];
      
      // First, block all timeslots that have trading order reservations
      tradingOrderReservations.forEach(timeSlot => {
        blockedTimeSlots.push(timeSlot);
        console.log(`🚫 BLOCKED - Trading order reservation active:`, timeSlot);
      });
      
      if (appointmentType === NORMAL_APPOINTMENT_TYPE) {
        console.log('🔍 Applying FREE appointment rules (1-user limit)');
        
        // Check all time slots that have orders or sessions
        const allTimeSlots = new Set([...Object.keys(orderCounts), ...Object.keys(sessionCounts)]);
        
        allTimeSlots.forEach(timeSlot => {
          // Skip timeslots that are already blocked by trading order reservations
          if (tradingOrderReservations.has(timeSlot)) {
            console.log(`⏭️ Skipping ${timeSlot} - already blocked by trading order reservation`);
            return;
          }
          
          const orderCount = orderCounts[timeSlot] || 0;
          const sessionCount = sessionCounts[timeSlot] || 0;
          const totalCount = orderCount + sessionCount;
          
          // Check if current user has an active session for this time slot
          const currentUserSessionCount = currentUserSessions[timeSlot] || 0;
          const currentUserHasSession = currentUserSessionCount > 0;
          
          // For blocking logic, exclude current user's session if they have one
          // This allows current user to continue with their session while blocking others
          const blockingOrderCount = orderCount;
          const blockingSessionCount = sessionCount - currentUserSessionCount; // Always subtract current user's sessions
          const blockingTotalCount = blockingOrderCount + blockingSessionCount;
          
          console.log('🔍 Time slot', timeSlot, 'has', orderCount, 'orders +', sessionCount, 'sessions =', totalCount, 'total');
          console.log('🔍 For blocking check (excluding current user session):', blockingOrderCount, 'orders +', blockingSessionCount, 'sessions =', blockingTotalCount, 'total');
          console.log('🔍 Current user has', currentUserSessionCount, 'session(s) for this slot:', currentUserHasSession);
          console.log('🔍 DEBUG - sessionCount:', sessionCount, 'currentUserSessionCount:', currentUserSessionCount, 'blockingSessionCount:', blockingSessionCount);
          console.log('🔍 Detailed breakdown for', timeSlot, ':', {
            orderCount,
            sessionCount,
            totalCount,
            currentUserSessionCount,
            blockingOrderCount,
            blockingSessionCount,
            blockingTotalCount,
            currentUserHasSession
          });
          
          // Block once one other user has selected the timeslot
          const maxUsers = 1;
          
          if (blockingTotalCount >= maxUsers) {
            blockedTimeSlots.push(timeSlot);
            console.log(`🚫 BLOCKED - ${blockingTotalCount} total users (${blockingOrderCount} orders + ${blockingSessionCount} sessions) >= ${maxUsers} limit:`, timeSlot);
          } else {
            console.log(`✅ AVAILABLE - time slot: ${timeSlot} (${blockingOrderCount} orders + ${blockingSessionCount} sessions = ${blockingTotalCount}/${maxUsers} users)`);
          }
        });
      } else if (appointmentType === 'special') {
        console.log('🔍 Applying SPECIAL appointment rules (3-user limit)');
        
        // Check all time slots that have orders or sessions
        const allTimeSlots = new Set([...Object.keys(orderCounts), ...Object.keys(sessionCounts)]);
        
        allTimeSlots.forEach(timeSlot => {
          // Skip timeslots that are already blocked by trading order reservations
          if (tradingOrderReservations.has(timeSlot)) {
            console.log(`⏭️ Skipping ${timeSlot} - already blocked by trading order reservation`);
            return;
          }
          
          const orderCount = orderCounts[timeSlot] || 0;
          const sessionCount = sessionCounts[timeSlot] || 0;
          const currentUserSessionCount = currentUserSessions[timeSlot] || 0;
          
          // Calculate blocking counts (excluding current user's sessions)
          const blockingOrderCount = orderCount;
          const blockingSessionCount = sessionCount - currentUserSessionCount;
          const blockingTotalCount = blockingOrderCount + blockingSessionCount;
          
          console.log('🔍 Time slot', timeSlot, 'has', orderCount, 'orders +', sessionCount, 'sessions =', orderCount + sessionCount, 'total');
          console.log('🔍 For blocking check (excluding current user session):', blockingOrderCount, 'orders +', blockingSessionCount, 'sessions =', blockingTotalCount, 'total');
          
          // Block if total users (excluding current user's session) reach the limit
          const maxUsers = 3; // Special appointments use 3-user limit
          
          if (blockingTotalCount >= maxUsers) {
            blockedTimeSlots.push(timeSlot);
            console.log(`🚫 BLOCKED - ${blockingTotalCount} total users (${blockingOrderCount} orders + ${blockingSessionCount} sessions) >= ${maxUsers} limit:`, timeSlot);
          } else {
            console.log(`✅ AVAILABLE - time slot: ${timeSlot} (${blockingOrderCount} orders + ${blockingSessionCount} sessions = ${blockingTotalCount}/${maxUsers} users)`);
          }
        });
      }
      
      console.log('🚫 Final blocked time slots:', blockedTimeSlots);
      setOccupiedTimeSlots(blockedTimeSlots);
      
    } catch (error) {
      console.error('❌ Error checking occupied time slots:', error);
      setOccupiedTimeSlots([]);
    }
    setLoadingTimeSlots(false);
  };

  // Session management functions
  const createUserSession = async () => {
    if (!auth.currentUser) return null;
    
    try {
      const sessionData = {
        userId: auth.currentUser.uid,
        page: 'appointment',
        timestamp: new Date(),
        lastActivity: new Date(),
        sessionId: Math.random().toString(36).substr(2, 9)
      };
      
      const sessionRef = doc(db, 'userSessions', auth.currentUser.uid);
      await setDoc(sessionRef, sessionData);
      
      sessionId.current = sessionData.sessionId;
      isSessionActive.current = true;
      
      console.log('User session created:', sessionData.sessionId);
      return sessionData.sessionId;
    } catch (error) {
      console.warn('Could not create user session (permission issue):', error);
      return null;
    }
  };

  const updateSessionActivity = async () => {
    if (!auth.currentUser || !isSessionActive.current) return;
    
    try {
      const sessionRef = doc(db, 'userSessions', auth.currentUser.uid);
      await setDoc(sessionRef, {
        lastActivity: new Date()
      }, { merge: true });
    } catch (error) {
      console.warn('Could not update session activity (permission issue):', error);
      // This is okay - session activity updates are not critical for core functionality
    }
  };

  const deleteUserSession = async () => {
    // Clean up browser session immediately (always do this)
    if (auth.currentUser) {
      const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      sessionStorage.removeItem(currentSessionKey);
      localStorage.removeItem(globalSessionKey);
      
      console.log('Browser and global appointment sessions cleaned up');
    }
    
    // Clean up local state
    isSessionActive.current = false;
    sessionId.current = null;
    
    // Only try to delete from Firestore if user is authenticated
    if (!auth.currentUser) {
      console.log('User not authenticated, skipping Firestore session deletion');
      return;
    }
    
    try {
      // Check if user is still authenticated before making Firestore calls
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('User authentication lost, skipping Firestore session deletion');
        return;
      }
      
      const sessionRef = doc(db, 'userSessions', currentUser.uid);
      await deleteDoc(sessionRef);
      console.log('Appointment user session deleted from Firestore');
    } catch (error) {
      // Log the error but don't throw - this is expected if user doesn't have permissions
      console.warn('Could not delete appointment user session from Firestore (permission issue):', error.message);
      // This is okay - the browser session is already cleaned up
    }
  };

  const checkForExistingSession = async () => {
    if (!auth.currentUser) return false;
    
    try {
      const sessionQuery = query(collection(db, 'userSessions'), where('userId', '==', auth.currentUser.uid));
      const sessionSnapshot = await getDocs(sessionQuery);
      
      if (!sessionSnapshot.empty) {
        const sessionData = sessionSnapshot.docs[0].data();
        const lastActivity = sessionData.lastActivity?.toDate();
        const now = new Date();
        
        // If session is older than 5 minutes, consider it stale
        if (lastActivity && (now - lastActivity) > 5 * 60 * 1000) {
          await deleteUserSession();
          return false;
        }
        
        // If session is for payment page, allow appointment page
        if (sessionData.page === 'payment') {
          return false;
        }
        
        // If session is for appointment page and active, prevent access
        if (sessionData.page === 'appointment' && sessionData.sessionId !== sessionId.current) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.warn('Could not check for existing session:', error);
      return false;
    }
  };



  // Delete active session
  const deleteActiveSession = async (sessionId) => {
    if (!sessionId) return;
    
    try {
      // First check if the document exists and belongs to the current user
      const sessionRef = doc(db, 'activeBookings', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        console.log('ℹ️ Session already deleted or does not exist:', sessionId);
        return;
      }
      
      const sessionData = sessionDoc.data();
      if (sessionData.userId !== auth.currentUser?.uid) {
        console.log('ℹ️ Cannot delete session - belongs to different user:', sessionId);
        return;
      }
      
      // Check if session has expired
      if (sessionData.expiresAt && sessionData.expiresAt.toDate() < new Date()) {
        console.log('ℹ️ Session has already expired:', sessionId);
        // Try to delete expired session anyway
        try {
          await deleteDoc(sessionRef);
          console.log('✅ Expired session deleted:', sessionId);
        } catch (deleteError) {
          console.log('ℹ️ Could not delete expired session (likely already cleaned up):', sessionId);
        }
        return;
      }
      
      await deleteDoc(sessionRef);
      console.log('✅ Active session deleted:', sessionId);
    } catch (error) {
      // Don't log as warning if it's just a permission issue for non-existent or non-owned sessions
      if (error.code === 'permission-denied') {
        console.log('ℹ️ Cannot delete session - permission denied (likely already deleted or not owned):', sessionId);
      } else if (error.code === 'not-found') {
        console.log('ℹ️ Session not found (already deleted):', sessionId);
      } else {
        console.warn('⚠️ Error deleting active session:', error);
      }
    }
  };

  // Clean up expired sessions (called periodically)
  const cleanupExpiredSessions = async () => {
    try {
      const now = new Date();
      const expiredSessionsQuery = query(
        collection(db, 'activeBookings'),
        where('expiresAt', '<', now)
      );
      
      const expiredSessionsSnapshot = await getDocs(expiredSessionsQuery);
      console.log(`🧹 Found ${expiredSessionsSnapshot.size} expired sessions to clean up`);
      
      for (const doc of expiredSessionsSnapshot.docs) {
        try {
          await deleteDoc(doc.ref);
          console.log('🧹 Cleaned up expired session:', doc.id);
    } catch (error) {
          console.log('ℹ️ Could not clean up expired session (likely already deleted):', doc.id);
        }
      }
    } catch (error) {
      console.log('ℹ️ Could not query expired sessions for cleanup:', error);
    }
  };

  // Reset entire booking process
  const resetBookingProcess = () => {
    console.log('Resetting booking process - cleaning up all sessions');
    
    // Clean up expired sessions first
    cleanupExpiredSessions();
    
    // Clean up active session IMMEDIATELY
    if (activeSessionId) {
      console.log('🧹 Deleting active session on reset:', activeSessionId);
      const sessionRef = doc(db, 'activeBookings', activeSessionId);
      deleteDoc(sessionRef).then(() => {
        console.log('✅ Active session deleted immediately on reset:', activeSessionId);
      }).catch((error) => {
        console.error('⚠️ Error deleting session on reset:', error);
      });
      setActiveSessionId(null);
    }
    
    // Clean up page session management
    if (auth.currentUser) {
      const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      sessionStorage.removeItem(currentSessionKey);
      localStorage.removeItem(globalSessionKey);
      
      // Reset session tracking
      isSessionActive.current = false;
      sessionId.current = null;
      
      console.log('Page session management cleaned up during reset');
    }
    
    // Reset form state
    setSelectedLocation('');
    setSelectedDate('');
    setSelectedTime('');
    setOrderId('');
    setDescription('');
    setTimeLeft(null);
    setTimerActive(false);
    setOccupiedTimeSlots([]);
  };

  // Session management effect - runs on component mount
  useEffect(() => {
    // Detect where user came from
    const referrer = document.referrer;
    const isFromServices = referrer.includes('/services') || location.state?.fromServices;
    setCameFromServices(isFromServices);
    
    const initializeSession = async () => {
      if (!auth.currentUser) return;
      
      // IMMEDIATE CHECK: Use localStorage for persistent conflict detection
      const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
      const paymentSessionKey = `payment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      // Check if this is a page refresh by looking for existing browser session
      const existingBrowserSession = sessionStorage.getItem(currentSessionKey);
      const isPageRefresh = existingBrowserSession !== null;
      
      if (isPageRefresh) {
        console.log('Page refresh detected - cleaning up browser session only');
        // Only clean up browser session on refresh, NOT global session
        sessionStorage.removeItem(currentSessionKey);
        
        // Also clean up any stale browser sessions
        const paymentSession = sessionStorage.getItem(paymentSessionKey);
        if (paymentSession) {
          try {
            const paymentData = JSON.parse(paymentSession);
            const age = Date.now() - paymentData.timestamp;
            if (age > 5 * 60 * 1000) {
              sessionStorage.removeItem(paymentSessionKey);
              console.log('Stale payment session cleaned up on refresh');
            }
          } catch (error) {
            sessionStorage.removeItem(paymentSessionKey);
          }
        }
      }
      
      // Check for global booking session first (persistent across browser restarts)
      const existingGlobalSession = localStorage.getItem(globalSessionKey);
      if (existingGlobalSession) {
        try {
          const globalSessionData = JSON.parse(existingGlobalSession);
          const now = Date.now();
          
          // If global session is older than 10 minutes, consider it stale
          if (now - globalSessionData.timestamp > 10 * 60 * 1000) {
            localStorage.removeItem(globalSessionKey);
            console.log('Stale global session removed');
          } else {
            // ANY active global session blocks new sessions (prevent bypass)
            console.log('Active global session detected - BLOCKING');
            
            // Prevent multiple redirects and handle cleanup
            if (handleRedirectCleanup()) {
              return;
            }
            
            const redirectPath = cameFromServices ? '/services' : '/';
            const redirectMessage = cameFromServices ? 'services page' : 'home page';
            
            if (globalSessionData.page === 'payment') {
              alert(`A payment page is already open. Redirecting to ${redirectMessage}.`);
            } else if (globalSessionData.page === 'appointment') {
              alert(`An appointment page is already open. Redirecting to ${redirectMessage}.`);
            } else {
              alert(`Another booking session is already active. Redirecting to ${redirectMessage}.`);
            }
            
            navigate(redirectPath);
            return;
          }
        } catch (error) {
          console.log('Invalid global session data, removing');
          localStorage.removeItem(globalSessionKey);
        }
      }
      
      // Check for payment page conflict (sessionStorage - current session only)
      const existingPaymentSession = sessionStorage.getItem(paymentSessionKey);
      if (existingPaymentSession) {
        try {
          const paymentSessionData = JSON.parse(existingPaymentSession);
          const now = Date.now();
          
          // If payment session is older than 5 minutes, consider it stale
          if (now - paymentSessionData.timestamp > 5 * 60 * 1000) {
            sessionStorage.removeItem(paymentSessionKey);
          } else {
            // Payment page is already open
            if (handleRedirectCleanup()) {
              return;
            }
            
            const redirectPath = cameFromServices ? '/services' : '/';
            const redirectMessage = cameFromServices ? 'services page' : 'home page';
            alert(`A payment page is already open. Redirecting to ${redirectMessage}.`);
            navigate(redirectPath);
            return;
          }
        } catch (error) {
          // Invalid session data, remove it
          sessionStorage.removeItem(paymentSessionKey);
        }
      }
      
      // Check for ANY existing appointment session (more aggressive check)
      const existingSession = sessionStorage.getItem(currentSessionKey);
      
      if (existingSession) {
        try {
          const sessionData = JSON.parse(existingSession);
          const now = Date.now();
          
          // If session is older than 5 minutes, consider it stale
          if (now - sessionData.timestamp > 5 * 60 * 1000) {
            sessionStorage.removeItem(currentSessionKey);
          } else {
            // Another appointment page is already open
            if (handleRedirectCleanup()) {
              return;
            }
            
            const redirectPath = cameFromServices ? '/services' : '/';
            const redirectMessage = cameFromServices ? 'services page' : 'home page';
            alert(`Another appointment page is already open. Redirecting to ${redirectMessage}.`);
            navigate(redirectPath);
            return;
          }
        } catch (error) {
          // Invalid session data, remove it
          sessionStorage.removeItem(currentSessionKey);
        }
      }
      
      // FINAL CHECK: Ensure no appointment session exists before creating new one
      const finalCheck = sessionStorage.getItem(currentSessionKey);
      if (finalCheck) {
        try {
          const finalSessionData = JSON.parse(finalCheck);
          const now = Date.now();
          
          // If session is older than 5 minutes, consider it stale
          if (now - finalSessionData.timestamp > 5 * 60 * 1000) {
            sessionStorage.removeItem(currentSessionKey);
          } else {
            // Another appointment session was created between our checks
            if (handleRedirectCleanup()) {
              return;
            }
            
            const redirectPath = cameFromServices ? '/services' : '/';
            const redirectMessage = cameFromServices ? 'services page' : 'home page';
            alert(`Another appointment page is already open. Redirecting to ${redirectMessage}.`);
            navigate(redirectPath);
            return;
          }
        } catch (error) {
          // Invalid session data, remove it
          sessionStorage.removeItem(currentSessionKey);
        }
      }
      
      // Check if appointment session was recently cleaned up (prevent recreation)
      const recentlyCleanedUpAppointment = sessionStorage.getItem('recently_cleaned_up_appointment');
      if (recentlyCleanedUpAppointment) {
        const cleanupTime = JSON.parse(recentlyCleanedUpAppointment);
        const timeSinceCleanup = Date.now() - cleanupTime;
        
        // If cleanup was less than 3 seconds ago, don't create new session
        if (timeSinceCleanup < 3000) {
          console.log('Appointment session was recently cleaned up, not creating new session');
          sessionStorage.removeItem('recently_cleaned_up_appointment');
          return;
        }
      }
      
      // Create browser session immediately
      const browserSession = {
        sessionId: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
        page: 'appointment',
        userId: auth.currentUser.uid,
        pageLoadId: pageLoadId.current
      };
      sessionStorage.setItem(currentSessionKey, JSON.stringify(browserSession));
      sessionId.current = browserSession.sessionId;
      isSessionActive.current = true;
      
      // Check if global session was recently cleaned up (prevent recreation)
      const recentlyCleanedUp = sessionStorage.getItem('recently_cleaned_up_global');
      if (recentlyCleanedUp) {
        const cleanupTime = JSON.parse(recentlyCleanedUp);
        const timeSinceCleanup = Date.now() - cleanupTime;
        
        // If cleanup was less than 5 seconds ago, don't recreate global session
        if (timeSinceCleanup < 5000) {
          console.log('Global session was recently cleaned up, not recreating');
          sessionStorage.removeItem('recently_cleaned_up_global');
        } else {
          // Create global session (persistent across browser restarts)
          const newGlobalSession = {
            sessionId: browserSession.sessionId,
            timestamp: Date.now(),
            page: 'appointment',
            userId: auth.currentUser.uid,
            browserSessionId: browserSession.sessionId,
            pageLoadId: pageLoadId.current,
            ownerTabId: pageLoadId.current, // Track which tab owns this session
            lastHeartbeat: Date.now()
          };
          localStorage.setItem(globalSessionKey, JSON.stringify(newGlobalSession));
          console.log('Global appointment session created:', newGlobalSession.sessionId);
        }
      } else {
        // Create global session (persistent across browser restarts)
        const newGlobalSession = {
          sessionId: browserSession.sessionId,
          timestamp: Date.now(),
          page: 'appointment',
          userId: auth.currentUser.uid,
          browserSessionId: browserSession.sessionId,
          pageLoadId: pageLoadId.current,
          ownerTabId: pageLoadId.current, // Track which tab owns this session
          lastHeartbeat: Date.now()
        };
        localStorage.setItem(globalSessionKey, JSON.stringify(newGlobalSession));
        console.log('Global appointment session created:', newGlobalSession.sessionId);
      }
      
      console.log('Browser appointment session created:', browserSession.sessionId);
      
      // Now check Firestore for additional conflicts (non-blocking)
      try {
        const hasExistingSession = await checkForExistingSession();
        if (hasExistingSession) {
          // Clean up browser session
          sessionStorage.removeItem(currentSessionKey);
          isSessionActive.current = false;
          sessionId.current = null;
          
          if (handleRedirectCleanup()) {
            return;
          }
          
          const redirectPath = cameFromServices ? '/services' : '/';
          const redirectMessage = cameFromServices ? 'services page' : 'home page';
          alert(`Another appointment page is already open. Redirecting to ${redirectMessage}.`);
          navigate(redirectPath);
          return;
        }
        
        // Create Firestore session
        await createUserSession();
      } catch (error) {
        console.warn('Firestore session check failed, continuing with browser session only:', error);
      }
      
      // Set up activity tracking and heartbeat system
      sessionCheckInterval.current = setInterval(() => {
        updateSessionActivity();
        // Also update browser session
        const updatedSession = {
          ...browserSession,
          timestamp: Date.now()
        };
        sessionStorage.setItem(currentSessionKey, JSON.stringify(updatedSession));
        
        // Update global session heartbeat (prevent bypass)
        const currentGlobalSession = localStorage.getItem(globalSessionKey);
        if (currentGlobalSession) {
          try {
            const globalSessionData = JSON.parse(currentGlobalSession);
            const updatedGlobalSession = {
              ...globalSessionData,
              timestamp: Date.now(),
              lastHeartbeat: Date.now()
            };
            localStorage.setItem(globalSessionKey, JSON.stringify(updatedGlobalSession));
          } catch (error) {
            console.warn('Error updating global session heartbeat:', error);
          }
        }
        
        // Periodic cleanup check - clean up any stale or orphaned sessions (more aggressive)
        const globalSession = localStorage.getItem(globalSessionKey);
        if (globalSession) {
          try {
            const globalSessionData = JSON.parse(globalSession);
            const age = Date.now() - globalSessionData.timestamp;
            const heartbeatAge = globalSessionData.lastHeartbeat ? Date.now() - globalSessionData.lastHeartbeat : 0;
            
            // More aggressive cleanup: 5 minutes for age, 30 seconds for heartbeat
            if (age > 5 * 60 * 1000 || heartbeatAge > 30 * 1000) {
              localStorage.removeItem(globalSessionKey);
              console.log('Stale/orphaned global session cleaned up during periodic check');
            }
          } catch (error) {
            localStorage.removeItem(globalSessionKey);
            console.log('Invalid global session cleaned up during periodic check');
          }
        }
      }, 15000); // Update every 15 seconds (more frequent for faster cleanup)
      
      // Listen for storage changes (other tabs) - IMMEDIATE conflict detection
      const handleStorageChange = (e) => {
        // Check for appointment session conflicts
        if (e.key === currentSessionKey && e.newValue) {
          try {
            const newSessionData = JSON.parse(e.newValue);
            console.log('Storage change detected:', newSessionData.sessionId, 'vs', browserSession.sessionId);
            
            // If another tab created a session, check if it's newer
            if (newSessionData.sessionId !== browserSession.sessionId) {
              console.log('Conflict detected - another appointment page opened');
              
              if (handleRedirectCleanup()) {
                return;
              }
              
              const redirectPath = cameFromServices ? '/services' : '/';
              const redirectMessage = cameFromServices ? 'services page' : 'home page';
              alert(`Another appointment page has been opened. Redirecting to ${redirectMessage}.`);
              navigate(redirectPath);
            }
          } catch (error) {
            console.warn('Error parsing storage change:', error);
          }
        }
        
        // Also check for global session conflicts
        if (e.key === globalSessionKey) {
          if (e.newValue) {
            try {
              const newGlobalSessionData = JSON.parse(e.newValue);
              console.log('Global session change detected:', newGlobalSessionData.sessionId, 'vs', browserSession.sessionId);
              
              // If another tab created a global session, check if it's different
              if (newGlobalSessionData.sessionId !== browserSession.sessionId && newGlobalSessionData.page === 'appointment') {
                console.log('Global session conflict detected - another appointment page opened');
                
                if (handleRedirectCleanup()) {
                  return;
                }
                
                const redirectPath = cameFromServices ? '/services' : '/';
                const redirectMessage = cameFromServices ? 'services page' : 'home page';
                alert(`Another appointment page has been opened. Redirecting to ${redirectMessage}.`);
                navigate(redirectPath);
              }
            } catch (error) {
              console.warn('Error parsing global session change:', error);
            }
          } else if (e.oldValue && !e.newValue) {
            // Global session was removed (another page redirected)
            console.log('Global session removed by another tab - cleaning up remaining page');
            
            // Clean up this page's session since the other page redirected
            if (auth.currentUser) {
              const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
              sessionStorage.removeItem(currentSessionKey);
              console.log('Browser session cleaned up after global session removal');
            }
            
            // REMOVED: Active booking session cleanup - no longer needed
            
            // Stop all intervals and cleanup
            if (sessionCheckInterval.current) {
              clearInterval(sessionCheckInterval.current);
              sessionCheckInterval.current = null;
            }
            
            // Reset session tracking
            isSessionActive.current = false;
            sessionId.current = null;
            
            console.log('Remaining page cleaned up after other page redirected');
          }
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      // Store the event listener for cleanup
      storageEventListener.current = handleStorageChange;
    };
    
    initializeSession();
    
    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
      // Remove storage event listener
      if (storageEventListener.current) {
        window.removeEventListener('storage', storageEventListener.current);
      }
      // Clean up browser and global sessions
      if (auth.currentUser) {
        const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
        const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
        
        sessionStorage.removeItem(currentSessionKey);
        
        // Clean up global session if this tab owns it
        const globalSession = localStorage.getItem(globalSessionKey);
        if (globalSession) {
          try {
            const globalSessionData = JSON.parse(globalSession);
            if (globalSessionData.ownerTabId === pageLoadId.current && globalSessionData.page === 'appointment') {
              localStorage.removeItem(globalSessionKey);
              console.log('Global appointment session cleaned up by owner tab on unmount');
            } else {
              console.log('Cannot clean up global session on unmount - not the owner tab');
            }
          } catch (error) {
            console.warn('Error parsing global session on unmount:', error);
          }
        }
        
        console.log('Browser and global appointment sessions cleaned up');
      }
      deleteUserSession();
    };
  }, []);

  // Clean up any stale sessions on component mount
  useEffect(() => {
    if (!auth.currentUser) return;
    
    console.log('Appointment component mounted - cleaning up stale sessions');
    
    // Clean up any stale global sessions
    const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
    const globalSession = localStorage.getItem(globalSessionKey);
    if (globalSession) {
      try {
        const globalSessionData = JSON.parse(globalSession);
        const age = Date.now() - globalSessionData.timestamp;
        
        // Ultra aggressive cleanup: 30 seconds for age, 5 seconds for heartbeat
        if (age > 30 * 1000 || (globalSessionData.lastHeartbeat && Date.now() - globalSessionData.lastHeartbeat > 5 * 1000)) {
          localStorage.removeItem(globalSessionKey);
          console.log('Stale global session cleaned up on mount');
        }
      } catch (error) {
        localStorage.removeItem(globalSessionKey);
        console.log('Invalid global session cleaned up on mount');
      }
    }
    
    // Clean up any stale browser sessions
    const appointmentSessionKey = `appointment_session_${auth.currentUser.uid}`;
    const paymentSessionKey = `payment_session_${auth.currentUser.uid}`;
    
    [appointmentSessionKey, paymentSessionKey].forEach(key => {
      const session = sessionStorage.getItem(key);
      if (session) {
        try {
          const sessionData = JSON.parse(session);
          const age = Date.now() - sessionData.timestamp;
          
          // Ultra aggressive cleanup: 2 seconds instead of 5, or if age is 0 (just created)
          if (age > 2 * 1000 || age === 0) {
            console.log('Appointment session is stale or just created, removing');
            sessionStorage.removeItem(appointmentSessionKey);
          } else {
            // Ultra aggressive cleanup: 15 seconds instead of 30
            if (age > 15 * 1000) {
              sessionStorage.removeItem(key);
              console.log('Stale browser session cleaned up on mount:', key);
            }
          }
        } catch (error) {
          sessionStorage.removeItem(key);
          console.log('Invalid browser session cleaned up on mount:', key);
        }
      }
    });
  }, []);

  // Effect to check occupied time slots when date changes
  useEffect(() => {
    if (selectedDate && selectedLocation === 'Chaguanas Branch') {
      // Add a small delay to prevent rapid successive calls
      const timeoutId = setTimeout(() => {
        checkOccupiedTimeSlots(selectedDate);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedDate, selectedLocation]);

  // Handle time selection from modal
  const handleTimeSelect = (selectedTimeSlot, sessionId) => {
    console.log('🔄 Time selected from modal:', selectedTimeSlot);
    
    // Store the pending selection for limit check
    setPendingTimeSelection(selectedTimeSlot);
    setPendingSessionId(sessionId);
    
    // Close the time select modal
    setIsTimeSelectModalOpen(false);
    
    // Open the limit check modal
    setIsLimitCheckModalOpen(true);
  };

  // Handle proceeding after limit check
  const handleProceedAfterLimitCheck = (selectedTimeSlot, sessionId) => {
    console.log('✅ Proceeding with timeslot after limit check:', selectedTimeSlot);
    
    setSelectedTime(selectedTimeSlot);
        if (sessionId) {
          setActiveSessionId(sessionId);
        }
    
    // Mark that we are continuing within the booking process
    continuingBookingProcess.current = true;
    
    // Start timer when time is selected (this is when timer should start)
    if (!timerActive) {
      console.log('⏰ Starting timer after timeslot selection');
      setTimeLeft(2 * 60); // 2 minutes in seconds
      setTimerActive(true);
    }
    
    // Close the limit check modal
    setIsLimitCheckModalOpen(false);
    
    // Clear pending selection
    setPendingTimeSelection('');
    setPendingSessionId(null);
  };

  // Handle closing limit check modal (user wants to select new time)
  const handleCloseLimitCheckModal = () => {
    console.log('🔄 User wants to select new time');
    
    // Clean up the session if it was created
    if (pendingSessionId) {
      deleteActiveSession(pendingSessionId);
    }
    
    // Close the limit check modal
    setIsLimitCheckModalOpen(false);
    
    // Clear pending selection
    setPendingTimeSelection('');
    setPendingSessionId(null);
    
    // Reopen the time select modal
    setIsTimeSelectModalOpen(true);
  };

  // Note: Active session creation is now handled by TimeSelect page

  // Clear selected time if it becomes unavailable (also delete any active session)
  useEffect(() => {
    if (selectedTime && occupiedTimeSlots.includes(selectedTime)) {
      if (activeSessionId) {
        deleteActiveSession(activeSessionId);
        setActiveSessionId(null);
      }
      setSelectedTime('');
    }
  }, [occupiedTimeSlots, selectedTime]);

  // Cleanup when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      console.log('Appointment component unmounting - cleaning up sessions');
      
      // REMOVED: Active booking session cleanup - no longer needed
      
      // Clean up browser and global sessions immediately
      if (auth.currentUser) {
        const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
        const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
        
        // Clean up browser session
        sessionStorage.removeItem(currentSessionKey);
        console.log('Browser appointment session cleaned up on unmount');
        
        // Clean up global session if this tab owns it
        const globalSession = localStorage.getItem(globalSessionKey);
        if (globalSession) {
          try {
            const globalSessionData = JSON.parse(globalSession);
            if (globalSessionData.ownerTabId === pageLoadId.current && globalSessionData.page === 'appointment') {
              localStorage.removeItem(globalSessionKey);
              console.log('Global appointment session cleaned up by owner tab on unmount');
            } else {
              console.log('Cannot clean up global session on unmount - not the owner tab or not appointment page');
            }
          } catch (error) {
            console.warn('Error parsing global session on unmount:', error);
            localStorage.removeItem(globalSessionKey);
          }
        }
        
        // Reset session tracking
        isSessionActive.current = false;
        sessionId.current = null;
      }
    };
  }, []);
  
  // Add cleanup function to window object for navigation links to call
  useEffect(() => {
    const cleanupAppointmentSession = () => {
      console.log('Manual cleanup triggered for appointment session');
      
      // REMOVED: Active booking session cleanup - no longer needed
      
      // Clean up browser and global sessions immediately
      if (auth.currentUser) {
        const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
        const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
        
        // Clean up browser session
        sessionStorage.removeItem(currentSessionKey);
        console.log('Browser appointment session cleaned up on manual cleanup');
        
        // Clean up global session if this tab owns it
        const globalSession = localStorage.getItem(globalSessionKey);
        if (globalSession) {
          try {
            const globalSessionData = JSON.parse(globalSession);
            if (globalSessionData.ownerTabId === pageLoadId.current && globalSessionData.page === 'appointment') {
              localStorage.removeItem(globalSessionKey);
              console.log('Global appointment session cleaned up by owner tab on manual cleanup');
            }
          } catch (error) {
            console.warn('Error parsing global session on manual cleanup:', error);
            localStorage.removeItem(globalSessionKey);
          }
        }
        
        // Reset session tracking
        isSessionActive.current = false;
        sessionId.current = null;
      }
    };
    
    // Add cleanup function to window object
    window.cleanupAppointmentSession = cleanupAppointmentSession;
    
    // Also add a more aggressive cleanup function for immediate use
    window.forceCleanupAppointmentSession = () => {
      console.log('Force cleanup triggered for appointment session');
      
      // REMOVED: Active booking session cleanup - no longer needed
      
      // Clean up browser and global sessions immediately
      if (auth.currentUser) {
        const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
        const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
        
        // Clean up browser session
        sessionStorage.removeItem(currentSessionKey);
        console.log('Browser appointment session force cleaned up');
        
        // Track that appointment session was recently cleaned up (prevent recreation)
        sessionStorage.setItem('recently_cleaned_up_appointment', JSON.stringify(Date.now()));
        console.log('Marked appointment session as recently cleaned up');
        
        // Force clean up global session regardless of ownership
        localStorage.removeItem(globalSessionKey);
        console.log('Global appointment session force cleaned up');
        
        // Track that global session was recently cleaned up (prevent recreation)
        sessionStorage.setItem('recently_cleaned_up_global', JSON.stringify(Date.now()));
        console.log('Marked global session as recently cleaned up');
        
        // Reset session tracking
        isSessionActive.current = false;
        sessionId.current = null;
      }
    };
    
    return () => {
      // Remove cleanup functions from window object
      delete window.cleanupAppointmentSession;
      delete window.forceCleanupAppointmentSession;
    };
  }, []);



  // Timer effect
  useEffect(() => {
    let interval = null;
    
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            setTimerActive(false);
            // Time expired - reset everything
            resetBookingProcess();
            console.log('⏰ Timer expired - redirecting to services page');
            navigate('/services');
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      setTimerActive(false);
    }
    
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  // Note: Timer is now started when returning from TimeSelect page, not when selectedTime is set

  // Reset everything when timer expires or page refreshes
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('Page unloading - cleaning up sessions');
      isUnloading.current = true;
      
      // Clean up browser session immediately
      if (auth.currentUser) {
        const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
        sessionStorage.removeItem(currentSessionKey);
        
        // Clean up global session when page is closed (only if same session)
        const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
        const globalSession = localStorage.getItem(globalSessionKey);
        if (globalSession) {
          try {
            const globalSessionData = JSON.parse(globalSession);
            // Only remove if this tab owns the session and it's an appointment session
            if (globalSessionData.ownerTabId === pageLoadId.current && globalSessionData.page === 'appointment') {
              localStorage.removeItem(globalSessionKey);
              console.log('Global appointment session cleaned up by owner tab');
            } else {
              console.log('Cannot clean up global session - not the owner tab');
            }
          } catch (error) {
            console.warn('Error parsing global session on close:', error);
          }
        }
      }
      
      // Also clean up active booking session on unload
      if (!continuingBookingProcess.current && activeSessionId) {
        deleteActiveSession(activeSessionId);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Clean up browser session when tab becomes hidden
        if (auth.currentUser) {
          const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
          sessionStorage.removeItem(currentSessionKey);
          
          // Clean up global session when tab becomes hidden
          const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
          const globalSession = localStorage.getItem(globalSessionKey);
          if (globalSession) {
            try {
              const globalSessionData = JSON.parse(globalSession);
              // Only remove if this is the same session (not payment session)
              if (globalSessionData.sessionId === sessionId.current && globalSessionData.page === 'appointment') {
                localStorage.removeItem(globalSessionKey);
                console.log('Global appointment session cleaned up on tab hidden');
              }
            } catch (error) {
              console.warn('Error parsing global session on tab hidden:', error);
            }
          }
        }
        
        // Also clean up active booking session when tab is hidden (user leaves)
        if (!continuingBookingProcess.current && activeSessionId) {
          deleteActiveSession(activeSessionId);
          setActiveSessionId(null);
        }
      } else if (document.visibilityState === 'visible') {
        // When tab becomes visible again, check if global session is stale
        if (auth.currentUser) {
          const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
          const globalSession = localStorage.getItem(globalSessionKey);
          if (globalSession) {
            try {
              const globalSessionData = JSON.parse(globalSession);
              const age = Date.now() - globalSessionData.timestamp;
              
              // If global session is older than 10 minutes, clean it up
              if (age > 10 * 60 * 1000) {
                localStorage.removeItem(globalSessionKey);
                console.log('Stale global session cleaned up on tab visible');
              }
              
              // Also check if this session belongs to a different page load (refresh detected)
              if (globalSessionData.pageLoadId && globalSessionData.pageLoadId !== pageLoadId.current) {
                localStorage.removeItem(globalSessionKey);
                console.log('Global session from different page load cleaned up on tab visible');
              }
            } catch (error) {
              console.warn('Error parsing global session on tab visible:', error);
            }
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log('Appointment component unmounting - cleaning up sessions IMMEDIATELY');
      
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Clean up active booking session on unmount (always delete since payment is not used)
      if (activeSessionId) {
        console.log('🧹 Cleaning up active session on unmount - activeSessionId:', activeSessionId);
        // Delete session immediately
        const sessionRef = doc(db, 'activeBookings', activeSessionId);
        deleteDoc(sessionRef).then(() => {
          console.log('✅ Active session deleted on unmount:', activeSessionId);
        }).catch((error) => {
          console.error('⚠️ Error deleting session on unmount:', error);
        });
      } else {
        console.log('ℹ️ No active session to clean up');
      }
    };
  }, [activeSessionId]);

  // Handle expired timer redirect from payment page
  useEffect(() => {
    if (isExpired && location.state?.orderDetails) {
      if (shouldResetBooking) {
        // Reset the entire booking process
        resetBookingProcess();
      } else {
        // Pre-fill the form with the previous selections
        setSelectedLocation(location.state.orderDetails.location || '');
        setSelectedDate(location.state.orderDetails.appointmentDate || '');
        setSelectedTime(location.state.orderDetails.appointmentTime || '');
      }
      
      // Clear the expired state from navigation
      navigate(location.pathname, { replace: true });
    }
  }, [isExpired, shouldResetBooking, location.state, navigate]);





  const handleBack = () => {
    console.log('Back button clicked - cleaning up sessions immediately');
    
    // Immediately clean up sessions before navigation
    if (auth.currentUser) {
      const currentSessionKey = `appointment_session_${auth.currentUser.uid}`;
      const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
      
      // Clean up browser session
      sessionStorage.removeItem(currentSessionKey);
      console.log('Browser session cleaned up on back navigation');
      
      // Clean up global session immediately
      localStorage.removeItem(globalSessionKey);
      console.log('Global session cleaned up on back navigation');
      
      // Reset session tracking
      isSessionActive.current = false;
      sessionId.current = null;
    }
    
    // Also clean up active booking session on back navigation
    if (activeSessionId) {
      deleteActiveSession(activeSessionId);
      setActiveSessionId(null);
    }
    
    navigate(-1);
  };

  // Add at top of component, after useState:
  const getAppointmentPrice = () => {
    // Check if this is a rebook scenario (special appointment that should be free)
    // We should allow free rebook if we have a valid rebook state OR if we're coming from rebook
    const hasValidRebookState = hasSpecialRebookState || (location.state?.specialIsFree && location.state?.isRebook);
    const isSpecialRebook = hasValidRebookState && appointmentType === 'special' && !hasUsedRebookState;
    
    if (appointmentType === NORMAL_APPOINTMENT_TYPE) {
      return 0;
    } else if (isSpecialRebook) {
      return 0;
    } else if (appointmentType === 'special') {
      return 30; // Fixed $30 appointment fee for special appointments
    }
    return 0;
  };

  // Calculate total cost (wash price + appointment fee)
  // Wash price uses the selected appointment date (July special = July timeslots only)
  const getTotalCost = () => {
    const appointmentPrice = getAppointmentPrice();
    let washPrice = 0;

    if (serviceData?.name) {
      washPrice = getWashPriceAmount(serviceData.name, selectedDate || new Date());
    } else if (serviceData?.price) {
      washPrice = parseInt(String(serviceData.price).replace('$', ''), 10) || 0;
    }

    return appointmentPrice + washPrice;
  };



  // Handle confirmation dialog response
  const handleBookingConfirmation = (isBookingForSomeoneElse) => {
    if (isBookingForSomeoneElse) {
      // User confirmed they're booking for someone else - proceed
      setSelectedTime(pendingTimeSelection);
    } else {
      // User cancelled - reset time selection
      setSelectedTime('');
    }
    
    // Reset confirmation dialog
    setShowBookingConfirmation(false);
    setPendingTimeSelection('');
  };

  // Add this function to check for a free appointment block
  // Location selection handler - simplified to just set location
  const handleLocationSelect = async (location) => {
    setSelectedLocation(location);
  };

  if (!serviceData) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#f9fafb', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center', color: '#1f2937' }}>
          <h2>No service selected</h2>
          <p>Please go back and select a service.</p>
          <button 
            onClick={() => navigate('/services')}
            style={{
              background: '#f97316',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            Go to Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f9fafb', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
        padding: '2.5rem',
        maxWidth: '600px',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f97316', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Schedule Your Appointment
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>
            Choose your preferred date and time for your car wash service
          </p>
        </div>

        {/* Expired Timer Message */}
        {isExpired && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #f87171',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: '#dc2626',
              marginBottom: '0.5rem'
            }}>
              ⏰ Time Expired
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#dc2626'
            }}>
              {shouldResetBooking 
                ? 'Your booking session has expired. Please select your location again to restart the booking process.'
                : 'Your booking session has expired. Your previous selections have been restored.'
              }
            </div>
          </div>
        )}



        {/* Service Summary */}
        <div style={{
          background: '#f9fafb',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ color: '#1f2937', marginBottom: '1rem' }}>Service Details</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ color: '#1f2937', marginBottom: '0.5rem' }}>{serviceData.name}</h4>
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{serviceData.description}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              {appointmentType && (
                <div>
                  <div style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Wash: ${serviceData?.name
                      ? `$${getWashPriceAmount(serviceData.name, selectedDate || new Date())}`
                      : (serviceData?.price || '$0')}
                  </div>
                  <div style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Appointment: ${getAppointmentPrice()}
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f97316', borderTop: '1px solid #e5e7eb', paddingTop: '0.25rem' }}>
                    Total: ${getTotalCost()}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ color: '#dc2626', fontSize: '1rem', marginTop: '0.5rem', fontWeight: 'bold' }}>
            Total is paid at location.
          </div>
        </div>

        {/* Location Selection */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.75rem', 
            fontWeight: 'bold', 
            color: '#1f2937' 
          }}>
            Select Location *
          </label>
          <select
            value={selectedLocation}
            onChange={(e) => {
              const newLocation = e.target.value;
              // If changing location, reset everything
              if (selectedLocation && newLocation !== selectedLocation) {
                resetBookingProcess();
              }
              handleLocationSelect(newLocation);
              // Reset appointment type and date selection when location changes
              setAppointmentType('');
              setSelectedDate('');
              setSelectedTime('');
              setShowDateSelection(false);
              setAvailableDates([]);
            }}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '1rem',
              background: '#fff'
            }}
          >
            <option value="">Choose a location...</option>
            {locations.map(location => (
              <option key={location.value} value={location.value}>
                {location.label}
              </option>
            ))}
          </select>
        </div>

        {/* Appointment Type Selection */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.75rem',
            fontWeight: 'bold',
            color: '#1f2937'
          }}>
            Appointment type *
          </label>
          {!selectedLocation ? (
            <div style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '1rem',
              background: '#f9fafb',
              color: '#6b7280',
              textAlign: 'center'
            }}>
              Please select a location first to choose appointment type
            </div>
          ) : (
            <select
              value={appointmentType}
              onChange={e => {
                const newType = e.target.value;
                handleAppointmentTypeChange(newType);
              }}
              disabled={loadingNormalCheck || loadingSpecialCheck}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '1rem',
                background: (loadingNormalCheck || loadingSpecialCheck) ? '#f9fafb' : '#fff',
                opacity: (loadingNormalCheck || loadingSpecialCheck) ? 0.7 : 1
              }}
            >
              <option value="">Choose appointment type...</option>
              <option value={NORMAL_APPOINTMENT_TYPE} disabled={!canBookNormalAppointment}>
                {getAppointmentTypeLabel(NORMAL_APPOINTMENT_TYPE, { plural: true })} {!canBookNormalAppointment && '(Blocked)'}
              </option>
            </select>
          )}

          {/* Blocking messages */}
          {!canBookNormalAppointment && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.9rem',
              color: '#dc2626',
              fontStyle: 'italic',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '0.75rem'
            }}>
              ⚠️ Normal appointments are currently blocked.
              {normalBlockDetails && (
                <div style={{ marginTop: '0.5rem', fontWeight: 'normal' }}>
                  {normalBlockDetails}
                </div>
              )}
            </div>
          )}


          {appointmentType === NORMAL_APPOINTMENT_TYPE && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.95rem',
              color: '#1f2937',
              fontWeight: 500,
              background: '#f3f4f6',
              borderRadius: '6px',
              padding: '0.75rem',
              textAlign: 'center'
            }}>
              One normal appointment is available every 3 days from last appointment date.

              <br />
              <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                Free Online Appointments
              </span>
              <br />
              <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                See FAQ for penalties for missing Normal Appointments
                <span style={{ marginLeft: 8 }}>
                  <Link to="/about" style={{ color: '#f97316', fontWeight: 'bold', textDecoration: 'underline' }}>click here</Link>
                </span>
              </span>
            </div>
          )}

          {appointmentType === NORMAL_APPOINTMENT_TYPE && (
            <div style={{
              marginTop: '1rem',
              marginBottom: '1rem',
              fontSize: '0.95rem',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#1f2937', fontSize: '1rem', fontWeight: 'bold' }}>
                Booking Information
              </h4>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#1f2937' }}>
                <li style={{ marginBottom: '0.35rem' }}>You can arrive up to 10 minutes early on the compound for your Normal Appointment. If the compound is not full, you can get a much earlier time.</li>
                <li style={{ marginBottom: '0.35rem' }}>Wash will start within 15 minutes of Arrival unless there is an emergency</li>
                <li style={{ marginBottom: '0.35rem' }}>Appointments can be given up 2 hours before appointment time</li>
              </ul>
              <p style={{ margin: '0.75rem 0 0 0', color: '#dc2626', fontWeight: 500 }}>
                Missing appointments will add penalty on normal appointment bookings
              </p>
            </div>
          )}
        </div>

        {/* Date and Time Selection - Only show after appointment type is selected */}
        {showDateSelection && (
          <>
            {/* Date Selection */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.75rem', 
                fontWeight: 'bold', 
                color: '#1f2937' 
              }}>
                Select Date *
              </label>
              <select
                value={selectedDate}
                onChange={async (e) => {
                  const selectedValue = e.target.value;
                  // Check if the selected date is disabled
                  const selectedDateObj = availableDates.find(date => date.value === selectedValue);
                  if (selectedDateObj && selectedDateObj.disabled) {
                    // Don't allow selection of disabled dates
                    return;
                  }
                  
                  // Delete previous active session if user is changing dates
                  if (activeSessionId && selectedDate !== selectedValue) {
                    try {
                      await deleteActiveSession(activeSessionId);
                      console.log('🧹 Deleted previous active session for date change');
                      setActiveSessionId(null);
                    } catch (error) {
                      console.warn('Could not delete previous active session:', error);
                      // Continue with date change even if session deletion fails
                    }
                  }
                  
                  setSelectedDate(selectedValue);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem',
                  background: '#fff'
                }}
              >
                <option value="">Choose a date...</option>
                {loadingDates ? (
                  <option value="" disabled>Loading dates...</option>
                ) : (
                  availableDates.map(date => (
                    <option 
                      key={date.value} 
                      value={date.value}
                      disabled={date.disabled}
                      style={{ 
                        color: date.disabled ? '#9ca3af' : '#000',
                        fontStyle: date.disabled ? 'italic' : 'normal'
                      }}
                    >
                      {date.disabled ? `${date.label} (Blocked)` : date.label}
                    </option>
                  ))
                )}
              </select>
              

            </div>

            {/* Timer Display */}
            {timerActive && timeLeft !== null && (
                <div style={{
                background: timeLeft <= 30 ? '#fee2e2' : timeLeft <= 60 ? '#fef3c7' : '#f0f9ff',
                border: timeLeft <= 30 ? '1px solid #f87171' : timeLeft <= 60 ? '1px solid #f59e0b' : '1px solid #0ea5e9',
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: timeLeft <= 30 ? '#dc2626' : timeLeft <= 60 ? '#d97706' : '#0c4a6e',
                  marginBottom: '0.25rem'
                }}>
                  Time Remaining: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  color: timeLeft <= 30 ? '#dc2626' : timeLeft <= 60 ? '#d97706' : '#0c4a6e',
                  fontStyle: 'italic'
                }}>
                  {timeLeft <= 30 ? 'Hurry! Time is running out!' : timeLeft <= 60 ? 'Complete your booking soon!' : 'Please complete your booking within the time limit'}
                </div>
            </div>
            )}

            {/* Time Selection */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.75rem', 
                fontWeight: 'bold', 
                color: '#1f2937' 
              }}>
                Select Time *
              </label>
              {!selectedDate ? (
                <div style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem',
                  background: '#f9fafb',
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  Please select a date first to view available times
                </div>
              ) : loadingTimeSlots ? (
                <div style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem',
                  background: '#f9fafb',
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  Checking available times...
                </div>
              ) : (
                <button
                  onClick={async () => {
                    // Delete current session if it exists
                    if (activeSessionId) {
                      try {
                        await deleteActiveSession(activeSessionId);
                        console.log('🧹 Deleted current session before opening time select modal');
                        setActiveSessionId(null);
                      } catch (error) {
                        console.warn('Could not delete current session:', error);
                      }
                    }
                    // Clear selected time since we're choosing a new one
                    setSelectedTime('');
                    // Stop the timer since we're changing times
                    if (timerActive) {
                      setTimerActive(false);
                      setTimeLeft(null);
                      console.log('⏰ Timer stopped - user is choosing new time');
                    }
                    // Open the modal
                    setIsTimeSelectModalOpen(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '1rem',
                    background: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#f3f4f6';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  {selectedTime ? `Selected: ${selectedTime}` : 'Choose Time'}
                </button>
              )}
              {selectedLocation === 'Chaguanas Branch' && occupiedTimeSlots.length > 0 && (
                <div style={{
                  fontSize: '0.8rem',
                  color: '#dc2626',
                  marginTop: '0.5rem'
                }}>
                  Some time slots are no longer available due to high demand.
                </div>
              )}
            </div>

            {/* Number Plate Field */}
            <div style={{ marginBottom: '2rem' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.75rem', 
            fontWeight: 'bold', 
            color: '#1f2937' 
          }}>
            Number Plate (Optional)
          </label>
              <div style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: '0.5rem',
                fontStyle: 'italic'
              }}>
                Optional: Enter number plate for faster processing at location. You can change vehicle on the day if needed.
              </div>
              <input
                type="text"
                placeholder="Enter Number Plate"
                value={numberPlate}
                onChange={(e) => setNumberPlate(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '1rem',
                  background: '#fff'
                }}
              />
            </div>

          </>
        )}

        {/* Appointment Summary */}
        {selectedLocation && selectedDate && selectedTime && (
          <div style={{
            background: '#f0f9ff',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            border: '1px solid #0ea5e9'
          }}>
            <h4 style={{ color: '#0c4a6e', marginBottom: '0.5rem' }}>Appointment Summary</h4>
            <p style={{ color: '#0c4a6e', margin: 0 }}>
              <strong>Location:</strong> {locations.find(l => l.value === selectedLocation)?.label}
            </p>
            <p style={{ color: '#0c4a6e', margin: 0 }}>
              <strong>Date:</strong> {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p style={{ color: '#0c4a6e', margin: 0 }}>
                              <strong>Time:</strong> {selectedTime}
            </p>
            <p style={{ color: '#0c4a6e', margin: 0 }}>
              <strong>Wash Cost:</strong> ${serviceData?.name
                ? getWashPriceAmount(serviceData.name, selectedDate || new Date())
                : (serviceData?.price || '$0')}
            </p>
            <p style={{ color: '#0c4a6e', margin: 0 }}>
              <strong>Appointment Fee:</strong> ${getAppointmentPrice()}
            </p>
            <p style={{ color: '#0c4a6e', margin: 0, fontWeight: 'bold', fontSize: '1.1rem' }}>
              <strong>Total Cost:</strong> ${getTotalCost()}
            </p>
            {numberPlate && (
              <p style={{ color: '#0c4a6e', margin: 0 }}>
                <strong>Number Plate:</strong> {numberPlate}
              </p>
            )}
          </div>
        )}

        {/* Info icon for Action Buttons */}
        <div style={{
          textAlign: 'center',
          marginBottom: '1rem',
          padding: '0.75rem',
          background: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '8px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            color: '#0c4a6e',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}>
            <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
            <span><strong>Action Buttons:</strong> Back to return to previous step, Complete Booking to finalize your appointment.</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <button
            onClick={handleBack}
            style={{
              background: '#f3f4f6',
              color: '#1f2937',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              flex: 1
            }}
          >
            Back
          </button>
          {appointmentType === NORMAL_APPOINTMENT_TYPE ? (
            <button
              onClick={async () => {
                if (!selectedLocation || !appointmentType || !selectedDate || !selectedTime || loading) return;
                
                // Check if user has contact number before proceeding
                const contactCheck = await checkUserContact();
                if (!contactCheck.hasContact) {
                  alert(contactCheck.message + '\n\nPlease go to your Account page to add your contact number.');
                  return;
                }
                
                // Check if number plate is already booked for this date
                const numberPlateCheck = await checkNumberPlateAvailability(numberPlate, selectedDate);
                if (!numberPlateCheck.isAvailable) {
                  alert(numberPlateCheck.message);
                  return;
                }
                
                setLoading(true);
                // Create order directly and show confirmation (bypass payment)
                try {
                  const washName = serviceData?.name || 'Car Wash Service';
                  const orderRef = await addDoc(collection(db, 'orders'), {
                    userId: auth.currentUser.uid,
                    location: selectedLocation,
                    appointmentType,
                    appointmentDate: selectedDate,
                    appointmentTime: selectedTime,
                    orderCode: generateOrderCode(),
                    description: description,
                    serviceName: washName,
                    serviceDescription: getAppointmentTypeLabel(NORMAL_APPOINTMENT_TYPE),
                    price: getTotalCost(),
                    status: 'confirmed',
                    createdAt: new Date(),
                    numberPlate: numberPlate || null,
                    contact: contactCheck.contact,
                  });
                  
                  // Generate QR code data using the Firestore document ID
                  const bookedOnDate = new Date();
                  const orderData = {
                    orderId: orderRef.id,
                    userId: auth.currentUser.uid,
                    appointmentDate: selectedDate,
                    appointmentTime: selectedTime,
                    location: selectedLocation,
                    status: 'confirmed',
                    appointmentType,
                    serviceName: washName,
                    serviceDescription: getAppointmentTypeLabel(NORMAL_APPOINTMENT_TYPE),
                    description: description,
                    price: getTotalCost(),
                    numberPlate: numberPlate || null,
                    bookedOn: `${bookedOnDate.getFullYear()}-${String(bookedOnDate.getMonth() + 1).padStart(2, '0')}-${String(bookedOnDate.getDate()).padStart(2, '0')}`
                  };
                  
                  const qrData = JSON.stringify({ type: 'order', orderData });
                  await updateDoc(orderRef, { orderId: orderRef.id, qrData });
                  
                  // Note: Special rebook state is only relevant for special appointments, not free appointments
                  alert('Booking complete! You will receive a confirmation email or SMS.');
                  resetBookingProcess();
                  navigate('/account?tab=orders');
                } catch (err) {
                  alert('Failed to complete booking. Please try again.');
                }
                setLoading(false);
              }}
              disabled={!selectedLocation || !appointmentType || !selectedDate || !selectedTime || loading}
              style={{
                background: selectedLocation && appointmentType && selectedDate && selectedTime ? '#f97316' : '#e5e7eb',
                color: selectedLocation && appointmentType && selectedDate && selectedTime ? '#fff' : '#9ca3af',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontWeight: 'bold',
                cursor: selectedLocation && appointmentType && selectedDate && selectedTime ? 'pointer' : 'not-allowed',
                flex: 1
              }}
            >
              {loading ? 'Processing...' : 'Complete Booking'}
            </button>
          ) : (
            <button
              onClick={async () => {
                console.log('🔍 Complete Booking button clicked for special appointment');
                console.log('🔍 Current state:', {
                  selectedLocation,
                  appointmentType,
                  selectedDate,
                  selectedTime,
                  loading,
                  serviceData: serviceData ? 'exists' : 'undefined'
                });
                
                if (!selectedLocation || !appointmentType || !selectedDate || !selectedTime || loading) {
                  console.log('❌ Button click blocked - missing required fields or loading');
                  return;
                }
                
                // Check if user has contact number before proceeding
                const contactCheck = await checkUserContact();
                if (!contactCheck.hasContact) {
                  alert(contactCheck.message + '\n\nPlease go to your Account page to add your contact number.');
                  return;
                }
                
                // Check if number plate is already booked for this date
                const numberPlateCheck = await checkNumberPlateAvailability(numberPlate, selectedDate);
                if (!numberPlateCheck.isAvailable) {
                  alert(numberPlateCheck.message);
                  return;
                }
                
                setLoading(true);
                
                try {
                  console.log('🚀 Creating special appointment order directly...');
                  
                  // Use the same simple logic as free appointments
                  const washName = serviceData?.name || 'Car Wash Service';
                  
                  console.log('📋 Creating special appointment order with data:', {
                    userId: auth.currentUser.uid,
                    serviceName: washName,
                    appointmentType: appointmentType,
                    price: getTotalCost()
                  });

                  const orderData = {
                    userId: auth.currentUser.uid,
                    location: selectedLocation,
                    appointmentType,
                    appointmentDate: selectedDate,
                    appointmentTime: selectedTime,
                    orderCode: generateOrderCode(),
                    serviceName: washName,
                    serviceDescription: `${serviceData?.description || 'Special Appointment Service'} (Special Appointment${hasSpecialRebookState ? ' - changed' : ''})`,
                    price: getTotalCost(),
                    status: 'confirmed',
                    createdAt: new Date(),
                    numberPlate: numberPlate || null,
                    contact: contactCheck.contact,
                  };

                  console.log('📋 Final order data to be saved:', orderData);
                  
                  try {
                    console.log('🔄 Attempting to save order to Firestore...');
                    const orderRef = await addDoc(collection(db, 'orders'), orderData);
                    
                    console.log('✅ Order saved with ID:', orderRef.id);
                    
                    // Generate QR code data using the Firestore document ID
                    const bookedOnDate = new Date();
                    const qrOrderData = {
                      orderId: orderRef.id,
                      userId: auth.currentUser.uid,
                      appointmentDate: selectedDate,
                      appointmentTime: selectedTime,
                      location: selectedLocation,
                      status: 'confirmed',
                      appointmentType,
                      serviceName: washName,
                      serviceDescription: `${serviceData?.description || 'Special Appointment Service'} (Special Appointment${hasSpecialRebookState ? ' - changed' : ''})`,
                      description: `${serviceData?.description || 'Special Appointment Service'} (Special Appointment${hasSpecialRebookState ? ' - changed' : ''})`,
                      price: getTotalCost(),
                      numberPlate: numberPlate || null,
                      bookedOn: `${bookedOnDate.getFullYear()}-${String(bookedOnDate.getMonth() + 1).padStart(2, '0')}-${String(bookedOnDate.getDate()).padStart(2, '0')}`
                    };
                    
                    const qrData = JSON.stringify({ type: 'order', orderData: qrOrderData });
                    await updateDoc(orderRef, { orderId: orderRef.id, qrData });
                    
                    console.log('✅ QR code data added to order');
                    
                    // Verify the order was actually saved
                    console.log('🔍 Verifying order was saved...');
                    const savedOrderDoc = await getDoc(orderRef);
                    if (savedOrderDoc.exists()) {
                      const savedData = savedOrderDoc.data();
                      console.log('✅ Order verification successful:', {
                        id: orderRef.id,
                        status: savedData.status,
                        userId: savedData.userId,
                        appointmentType: savedData.appointmentType
                      });
                    } else {
                      console.log('❌ Order verification failed - order does not exist');
                    }
                  } catch (saveError) {
                    console.error('❌ Error saving order to Firestore:', saveError);
                    console.error('❌ Error details:', {
                      code: saveError.code,
                      message: saveError.message,
                      stack: saveError.stack
                    });
                    throw saveError;
                  }
                  
                  console.log('✅ Special appointment order created successfully');
                  
                  // Clear the special rebook state after successful order completion
                  if (hasSpecialRebookState) {
                    try {
                      console.log('🔄 Clearing special rebook state after successful special appointment completion...');
                      const rebookStatesQuery = query(
                        collection(db, 'specialRebookStates'),
                        where('userId', '==', auth.currentUser.uid),
                        where('rebookType', '==', 'special')
                      );
                      const querySnapshot = await getDocs(rebookStatesQuery);
                      if (!querySnapshot.empty) {
                        await deleteDoc(doc(db, 'specialRebookStates', querySnapshot.docs[0].id));
                        console.log('✅ Special rebook state cleared after successful special appointment completion');
                      }
                    } catch (error) {
                      console.error('❌ Error clearing rebook state after special appointment completion:', error);
                    }
                  }
                  
                  alert('Special Appointment booking complete! You will receive a confirmation email or SMS.');
                  resetBookingProcess();
                  navigate('/account?tab=orders');
                  
                } catch (error) {
                  console.error('❌ Error creating special appointment order:', error);
                  console.error('❌ Error details:', error.code, error.message);
                  alert('There was an error creating your special appointment. Please try again.');
                }
                setLoading(false);
              }}
              disabled={!selectedLocation || !appointmentType || !selectedDate || !selectedTime || loading}
              style={{
                background: selectedLocation && appointmentType && selectedDate && selectedTime ? '#f97316' : '#e5e7eb',
                color: selectedLocation && appointmentType && selectedDate && selectedTime ? '#fff' : '#9ca3af',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontWeight: 'bold',
                cursor: selectedLocation && appointmentType && selectedDate && selectedTime ? 'pointer' : 'not-allowed',
                flex: 1
              }}
            >
              {loading ? 'Processing...' : 'Complete Booking'}
            </button>
          )}
        </div>

        {/* Special Appointment Booking Confirmation Dialog */}
        {showBookingConfirmation && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <h3 style={{
                color: '#1f2937',
                marginBottom: '1rem',
                fontSize: '1.25rem',
                fontWeight: 'bold'
              }}>
                You already have a booking for this time
              </h3>
              <p style={{
                color: '#6b7280',
                marginBottom: '1.5rem',
                lineHeight: '1.5'
              }}>
                You already have {userSpecialBookings[pendingTimeSelection]} special appointment(s) for {pendingTimeSelection} on {formatDate(new Date(selectedDate + 'T00:00:00'))}.
              </p>
              <p style={{
                color: '#6b7280',
                marginBottom: '1.5rem',
                lineHeight: '1.5'
              }}>
                Are you booking this appointment for someone else?
              </p>
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => handleBookingConfirmation(false)}
                  style={{
                    background: '#f3f4f6',
                    color: '#1f2937',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  No, Cancel
                </button>
                <button
                  onClick={() => handleBookingConfirmation(true)}
                  style={{
                    background: '#f97316',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Yes, Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Time Select Modal */}
        <TimeSelectModal
          isOpen={isTimeSelectModalOpen}
          onClose={() => setIsTimeSelectModalOpen(false)}
          onTimeSelect={handleTimeSelect}
          selectedDate={selectedDate}
          selectedLocation={selectedLocation}
          appointmentType={appointmentType}
        />

        {/* Limit Check Modal */}
        <LimitCheckModal
          isOpen={isLimitCheckModalOpen}
          onClose={handleCloseLimitCheckModal}
          onProceed={handleProceedAfterLimitCheck}
          selectedTimeSlot={pendingTimeSelection}
          selectedDate={selectedDate}
          selectedLocation={selectedLocation}
          appointmentType={appointmentType}
          sessionId={pendingSessionId}
        />
      </div>
    </div>
  );
}

export default Appointment; 